import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Rocket,
  Play,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  GitCommit,
  User,
  FileCode,
  ChevronDown,
  ChevronUp,
  Server,
  Globe,
  Mail,
  Bot,
  StopCircle,
  Undo2,
  Database,
  Cloud,
  SkipForward,
  Ban,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { getAuthToken } from '../../services/api/client';
import {
  cancelRemediation as cancelRemediationRequest,
  declineDeploymentRemediation,
  getDeployment,
  listActiveRemediations,
  listDeployments,
  respondToDeployment,
  triggerDeployment as triggerDeploymentRequest,
} from '../../services/deploymentsApi';

const DeploymentsManager = () => {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploymentError, setDeploymentError] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState('all');
  const [expandedDeployment, setExpandedDeployment] = useState(null);
  const [deploymentDetails, setDeploymentDetails] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, target: null });

  // Active remediations state
  const [activeRemediations, setActiveRemediations] = useState([]);
  const [cancelling, setCancelling] = useState(null);
  const [cancelResult, setCancelResult] = useState(null);

  // Response dialog state
  const [responseDialog, setResponseDialog] = useState({ open: false, deployment: null, questions: [] });
  const [responses, setResponses] = useState({});
  const [submittingResponse, setSubmittingResponse] = useState(false);

  // Ref to track deployments for polling without causing re-renders
  const deploymentsRef = useRef([]);
  // Ref to track in-flight request and abort if needed
  const abortControllerRef = useRef(null);
  const requestInFlightRef = useRef(false);

  // Load deployments list with request deduplication
  const loadDeployments = useCallback(async (isPolling = false) => {
    if (!getAuthToken()) {
      console.warn('No admin auth token found');
      setDeploymentError('Admin authentication is required to load deployments.');
      setLoading(false);
      return;
    }

    // Skip if request already in flight (prevents stacking)
    if (requestInFlightRef.current) {
      // Skip duplicate request
      return;
    }

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    requestInFlightRef.current = true;

    try {
      // Only show loading spinner on initial load, not polling
      if (!isPolling) {
        setLoading(true);
        setDeploymentError(null);
      }

      const data = await listDeployments(50, { signal: controller.signal });
      const newDeployments = data.deployments || [];
      setDeployments(newDeployments);
      deploymentsRef.current = newDeployments;
      setDeploymentError(null);
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError') return;
      console.error('Failed to load deployments:', error);
      setDeploymentError(error.message || 'Unable to load deployments.');
    } finally {
      requestInFlightRef.current = false;
      if (!isPolling) setLoading(false);
    }
  }, []);

  // Load deployment details
  const loadDeploymentDetails = useCallback(async (deploymentId, forceReload = false) => {
    if (!getAuthToken()) return;

    // Skip if already loaded and not forcing reload
    if (!forceReload && deploymentDetails[deploymentId]) return;

    try {
      const data = await getDeployment(deploymentId);
      setDeploymentDetails(prev => ({ ...prev, [deploymentId]: data }));
    } catch (error) {
      console.error('Failed to load deployment details:', error);
    }
  }, [deploymentDetails]);

  // Load active remediations
  const loadActiveRemediations = useCallback(async () => {
    if (!getAuthToken()) return;

    try {
      const data = await listActiveRemediations();
      setActiveRemediations(data.active_remediations || []);
    } catch (error) {
      console.error('Failed to load active remediations:', error);
    }
  }, []);

  // Cancel remediation
  const cancelRemediation = async (remediationId, revertChanges = false) => {
    if (!getAuthToken()) return;

    setCancelling(remediationId);
    try {
      const result = await cancelRemediationRequest(remediationId, {
        revert_changes: revertChanges,
        reason: 'Cancelled by admin',
      });
      setCancelResult(result);

      // Refresh lists
      await loadActiveRemediations();
      await loadDeployments();
    } catch (error) {
      console.error('Failed to cancel remediation:', error);
      alert('Failed to cancel remediation: ' + error.message);
    } finally {
      setCancelling(null);
    }
  };

  // Submit response to remediation questions
  const submitResponse = async () => {
    if (!getAuthToken()) return;

    const { deployment, questions } = responseDialog;
    if (!deployment) return;

    // Validate all questions have answers
    const hasEmptyAnswers = questions.some((_, idx) => !responses[idx]?.trim());
    if (hasEmptyAnswers) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setSubmittingResponse(true);
    try {
      await respondToDeployment(
        deployment.id,
        questions.map((q, idx) => ({
          question: q,
          answer: responses[idx],
        })),
      );

      alert('Responses submitted successfully! The deployment will continue with your answers.');

      // Close dialog and refresh
      setResponseDialog({ open: false, deployment: null, questions: [] });
      setResponses({});
      await loadDeployments();
    } catch (error) {
      console.error('Failed to submit responses:', error);
      alert('Failed to submit responses: ' + error.message);
    } finally {
      setSubmittingResponse(false);
    }
  };

  // Decline remediation
  const declineRemediation = async (deploymentId) => {
    if (!getAuthToken()) return;

    try {
      await declineDeploymentRemediation(deploymentId);

      alert('Remediation declined. The deployment has been marked as failed.');
      await loadDeployments();
    } catch (error) {
      console.error('Failed to decline remediation:', error);
      alert('Failed to decline remediation: ' + error.message);
    }
  };

  // Trigger deployment
  const triggerDeployment = async (target) => {
    if (!getAuthToken()) {
      alert('Admin authentication required');
      return;
    }

    setDeploying(true);
    try {
      await triggerDeploymentRequest(target);
      // Deployment triggered successfully

      // Refresh deployments list
      await loadDeployments();
    } catch (error) {
      console.error('Failed to trigger deployment:', error);
      alert('Failed to trigger deployment: ' + error.message);
    } finally {
      setDeploying(false);
      setConfirmDialog({ open: false, target: null });
    }
  };

  // Initial load and polling
  useEffect(() => {
    loadDeployments(false);
    loadActiveRemediations();

    // Poll for updates every 10 seconds if there are active deployments or remediations
    const interval = setInterval(() => {
      const hasActive = deploymentsRef.current.some(d =>
        ['queued', 'building', 'installing', 'deploying', 'retrying', 'auto_remediating'].includes(d.status)
      );
      if (hasActive) {
        loadDeployments(true); // Pass true to indicate polling (skip loading spinner)
      }
      // Always check for active remediations during polling
      loadActiveRemediations();
    }, 10000);

    // Cleanup: abort any in-flight request
    return () => {
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadDeployments, loadActiveRemediations]);

  // Reload details for expanded deployment when deployments list changes
  useEffect(() => {
    if (expandedDeployment) {
      loadDeploymentDetails(expandedDeployment, true);
    }
  }, [deployments, expandedDeployment, loadDeploymentDetails]);

  // Check if a deployment is stale (in active state for > 10 minutes)
  const isStaleDeployment = (deployment) => {
    const activeStatuses = ['building', 'installing', 'deploying', 'auto_remediating', 'queued', 'retrying'];
    if (!activeStatuses.includes(deployment.status)) return false;

    const createdAt = new Date(deployment.created_at);
    const now = new Date();
    const diffMinutes = (now - createdAt) / (1000 * 60);
    return diffMinutes > 10;
  };

  // Status badge styling
  const getStatusBadge = (status, deployment = null) => {
    const variants = {
      completed: { variant: 'default', className: 'bg-green-500', icon: CheckCircle2 },
      failed: { variant: 'destructive', icon: XCircle },
      queued: { variant: 'secondary', icon: Clock },
      building: { variant: 'default', className: 'bg-blue-500', icon: Loader2 },
      installing: { variant: 'default', className: 'bg-blue-500', icon: Loader2 },
      deploying: { variant: 'default', className: 'bg-amber-500', icon: Rocket },
      retrying: { variant: 'default', className: 'bg-orange-500', icon: RefreshCw },
      awaiting_input: { variant: 'default', className: 'bg-purple-500', icon: Mail },
      auto_remediating: { variant: 'default', className: 'bg-purple-500', icon: Bot },
      cancelled: { variant: 'default', className: 'bg-gray-500', icon: StopCircle },
      superseded: { variant: 'default', className: 'bg-gray-500', icon: SkipForward },
      skipped: { variant: 'default', className: 'bg-gray-500', icon: SkipForward },
      orphaned: { variant: 'default', className: 'bg-gray-500', icon: AlertTriangle },
      tests_failed: { variant: 'destructive', icon: Ban },
      remediation_error: { variant: 'destructive', icon: XCircle },
      remediation_timeout: { variant: 'destructive', icon: Clock },
    };

    const config = variants[status] || { variant: 'outline', icon: AlertTriangle };
    const Icon = config.icon;

    // Don't animate if stale (stuck in active state for > 10 min)
    const isStale = deployment && isStaleDeployment(deployment);
    const isAnimated = ['building', 'installing', 'deploying'].includes(status) && !isStale;

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className={`h-3 w-3 mr-1 ${isAnimated ? 'animate-spin' : ''}`} />
        {status}{isStale ? ' (stale)' : ''}
      </Badge>
    );
  };

  // Type badge
  const getTypeBadge = (type) => {
    if (type === 'backend') {
      return (
        <Badge variant="outline" className="border-purple-500 text-purple-600">
          <Server className="h-3 w-3 mr-1" />
          Backend
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="border-blue-500 text-blue-600">
        <Globe className="h-3 w-3 mr-1" />
        Frontend
      </Badge>
    );
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m`;
  };

  // Handle expand/collapse
  const toggleExpand = (deploymentId) => {
    if (expandedDeployment === deploymentId) {
      setExpandedDeployment(null);
    } else {
      setExpandedDeployment(deploymentId);
      loadDeploymentDetails(deploymentId);
    }
  };

  const hasAdminSession = Boolean(getAuthToken());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Deployments</h2>
          <p className="text-muted-foreground">
            Monitor and trigger deployments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadDeployments(false)}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {deploymentError && (
        <Card className="border-red-500" role="alert">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-4 text-red-600">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5" />
                <div>
                  <p className="font-medium">Deployment history could not be loaded.</p>
                  <p className="text-sm">{deploymentError}</p>
                </div>
              </div>
              {hasAdminSession && (
                <Button variant="outline" size="sm" onClick={() => loadDeployments(false)}>
                  Retry
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Deploy Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5" />
            Quick Deploy
          </CardTitle>
          <CardDescription>
            Manually trigger a deployment. The system will pull latest code and deploy.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Select value={selectedTarget} onValueChange={setSelectedTarget}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select target" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All (Backend + Frontends)</SelectItem>
                <SelectItem value="backend">Backend Only</SelectItem>
                <SelectItem value="public">Public Site</SelectItem>
                <SelectItem value="admin">Admin Portal</SelectItem>
                <SelectItem value="members">Members Portal</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => setConfirmDialog({ open: true, target: selectedTarget })}
              disabled={deploying || !hasAdminSession}
            >
              {deploying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deploying...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Deploy
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Auto-Remediations Card */}
      {activeRemediations.length > 0 && (
        <Card className="border-purple-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              Active Auto-Remediations
            </CardTitle>
            <CardDescription>
              Claude is currently attempting to fix deployment issues. You can cancel these processes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeRemediations.map((remediation) => (
                <div
                  key={remediation.id}
                  className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-purple-500 text-purple-600">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          {remediation.type === 'test' ? 'Fixing Tests' : 'Fixing Deployment'}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          Running for {Math.floor(remediation.running_seconds / 60)}m {Math.floor(remediation.running_seconds % 60)}s
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">ID: </span>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {remediation.id.slice(0, 8)}
                        </code>
                        {remediation.pre_commit_sha && (
                          <>
                            <span className="text-muted-foreground ml-3">Pre-commit: </span>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              {remediation.pre_commit_sha.slice(0, 8)}
                            </code>
                          </>
                        )}
                      </div>
                      {remediation.output_preview && (
                        <pre className="text-xs text-muted-foreground bg-muted/50 p-2 rounded max-h-20 overflow-y-auto">
                          {remediation.output_preview}
                        </pre>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => cancelRemediation(remediation.id, false)}
                        disabled={cancelling === remediation.id}
                      >
                        {cancelling === remediation.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <StopCircle className="h-4 w-4 mr-1" />
                            Cancel
                          </>
                        )}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => cancelRemediation(remediation.id, true)}
                        disabled={cancelling === remediation.id}
                      >
                        {cancelling === remediation.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Undo2 className="h-4 w-4 mr-1" />
                            Cancel & Revert
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deployments List */}
      <Card>
        <CardHeader>
          <CardTitle>Deployment History</CardTitle>
          <CardDescription>
            Recent deployments with status and logs
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && deployments.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : deploymentError ? (
            <div className="text-center py-8 text-muted-foreground">
              Deployment history is unavailable
            </div>
          ) : deployments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No deployments found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deployments.map((deployment) => (
                  <Collapsible
                    key={deployment.id}
                    open={expandedDeployment === deployment.id}
                    onOpenChange={() => toggleExpand(deployment.id)}
                    asChild
                  >
                    <>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleExpand(deployment.id)}
                      >
                        <TableCell>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              {expandedDeployment === deployment.id ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </TableCell>
                        <TableCell>{getStatusBadge(deployment.status, deployment)}</TableCell>
                        <TableCell>{getTypeBadge(deployment.type)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {deployment.trigger || 'manual'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(deployment.created_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDuration(deployment.duration_seconds) || (
                            ['building', 'deploying', 'installing', 'auto_remediating'].includes(deployment.status) && !isStaleDeployment(deployment)
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {deployment.commit_sha && (
                              <code className="text-xs bg-muted px-1 py-0.5 rounded font-mono">
                                {deployment.commit_sha}
                              </code>
                            )}
                            {deployment.targets && (
                              <span className="text-xs text-muted-foreground">
                                {deployment.targets.join(', ')}
                              </span>
                            )}
                            {deployment.reason && !deployment.targets && (
                              <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={deployment.reason}>
                                {deployment.reason}
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={6} className="p-4">
                            <DeploymentDetails
                              deployment={deployment}
                              details={deploymentDetails[deployment.id]}
                              onRespond={(deployment, questions) => {
                                setResponseDialog({
                                  open: true,
                                  deployment,
                                  questions,
                                });
                                setResponses({});
                              }}
                              onDecline={declineRemediation}
                            />
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirm Deploy Dialog */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, target: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deployment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deploy <strong>{confirmDialog.target}</strong>?
              This will pull the latest code from GitHub and deploy to production.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => triggerDeployment(confirmDialog.target)}>
              Deploy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Result Dialog */}
      <AlertDialog
        open={cancelResult !== null}
        onOpenChange={(open) => !open && setCancelResult(null)}
      >
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {cancelResult?.success ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Remediation Cancelled
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-500" />
                  Cancellation Issue
                </>
              )}
            </AlertDialogTitle>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            {/* Revert Result */}
            {cancelResult?.revert_result && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Undo2 className="h-4 w-4" />
                  <span className="font-medium">Git Revert</span>
                  <Badge variant={cancelResult.revert_result.success ? 'default' : 'destructive'}>
                    {cancelResult.revert_result.method || 'no changes'}
                  </Badge>
                </div>
                {cancelResult.revert_result.reverted_commits?.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    Reverted {cancelResult.revert_result.reverted_commits.length} commit(s)
                  </div>
                )}
                {cancelResult.revert_result.stashed_changes && (
                  <div className="text-sm text-amber-600">
                    Uncommitted changes were stashed
                  </div>
                )}
              </div>
            )}

            {/* Detected Operations */}
            {cancelResult?.detected_operations?.has_infrastructure_changes && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-amber-700 dark:text-amber-300">
                    Infrastructure Changes Detected
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  {cancelResult.detected_operations.aws_operations?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Cloud className="h-4 w-4 text-amber-600 mt-0.5" />
                      <div>
                        <span className="text-amber-700 dark:text-amber-300">AWS: </span>
                        {cancelResult.detected_operations.aws_operations.join(', ')}
                      </div>
                    </div>
                  )}
                  {cancelResult.detected_operations.mongodb_operations?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Database className="h-4 w-4 text-amber-600 mt-0.5" />
                      <div>
                        <span className="text-amber-700 dark:text-amber-300">MongoDB: </span>
                        {cancelResult.detected_operations.mongodb_operations.join(', ')}
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  These changes may require manual review and cannot be automatically reverted.
                </p>
              </div>
            )}

            {/* Warnings */}
            {cancelResult?.warnings?.length > 0 && (
              <div className="space-y-2">
                {cancelResult.warnings.map((warning, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-amber-600">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{warning}</span>
                  </div>
                ))}
              </div>
            )}

            {/* File Changes */}
            {cancelResult?.detected_operations?.file_changes?.length > 0 && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileCode className="h-4 w-4" />
                  <span className="font-medium">File Changes ({cancelResult.detected_operations.file_changes.length})</span>
                </div>
                <div className="max-h-24 overflow-y-auto">
                  {cancelResult.detected_operations.file_changes.slice(0, 10).map((change, idx) => (
                    <code key={idx} className="block text-xs text-muted-foreground">
                      {change}
                    </code>
                  ))}
                  {cancelResult.detected_operations.file_changes.length > 10 && (
                    <span className="text-xs text-muted-foreground">
                      ... and {cancelResult.detected_operations.file_changes.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setCancelResult(null)}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Response Dialog */}
      <AlertDialog
        open={responseDialog.open}
        onOpenChange={(open) => !open && setResponseDialog({ open: false, deployment: null, questions: [] })}
      >
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              Respond to Auto-Remediation Questions
            </AlertDialogTitle>
            <AlertDialogDescription>
              The auto-remediation system needs your input to continue fixing the deployment issue.
              Please answer all questions below.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            {responseDialog.questions.map((question, idx) => (
              <div key={idx} className="space-y-2">
                <label className="text-sm font-medium text-purple-700 dark:text-purple-300">
                  Question {idx + 1}:
                </label>
                <p className="text-sm text-muted-foreground bg-purple-50 dark:bg-purple-950/30 p-3 rounded border border-purple-200 dark:border-purple-800">
                  {question}
                </p>
                <textarea
                  className="w-full min-h-[80px] p-3 border rounded-md bg-background"
                  placeholder="Your answer..."
                  value={responses[idx] || ''}
                  onChange={(e) => setResponses({ ...responses, [idx]: e.target.value })}
                />
              </div>
            ))}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={submittingResponse}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitResponse} disabled={submittingResponse}>
              {submittingResponse ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Submit Responses
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Deployment Details Component
const DeploymentDetails = ({ deployment: _deployment, details, onRespond, onDecline }) => {
  if (!details) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Determine the triggering error for display
  const triggeringError = details.reason || details.details?.error;
  const triggeringStep = details.details?.step;

  return (
    <div className="space-y-4">
      {/* Triggering Error - shown prominently at top */}
      {triggeringError && (
        <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-700 dark:text-red-300">
              {triggeringStep ? `Error at step: ${triggeringStep}` : 'Error'}
            </span>
          </div>
          <pre className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap max-h-40 overflow-y-auto">
            {triggeringError}
          </pre>
        </div>
      )}

      {/* Test Results - only show if tests actually failed */}
      {details.test_result && (
        (details.test_result.frontend?.success === false) ||
        (details.test_result.backend?.success === false)
      ) && (
        <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 mb-2">
            <Ban className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-700 dark:text-red-300">Test Results</span>
          </div>
          {details.test_result.frontend?.success === false && (
            <div className="mb-2">
              <span className="text-xs font-medium text-red-600">Frontend:</span>
              <pre className="text-xs text-red-500 whitespace-pre-wrap mt-1 max-h-32 overflow-y-auto">
                {details.test_result.frontend.error || details.test_result.frontend.stderr || 'Failed'}
              </pre>
            </div>
          )}
          {details.test_result.backend?.success === false && (
            <div>
              <span className="text-xs font-medium text-red-600">Backend:</span>
              <pre className="text-xs text-red-500 whitespace-pre-wrap mt-1 max-h-32 overflow-y-auto">
                {details.test_result.backend.error || details.test_result.backend.stderr || 'Failed'}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Superseded/Orphaned Info */}
      {(details.status === 'superseded' || details.status === 'orphaned') && (
        <div className="p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-1">
            {details.status === 'superseded' ? (
              <SkipForward className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            )}
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
              {details.status}
            </span>
          </div>
          {(details.reason || details.details?.reason || details.orphaned_reason) && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {details.reason || details.details?.reason || details.orphaned_reason}
            </p>
          )}
        </div>
      )}

      {/* Commit Info */}
      {details.commit_info && (
        <div className="flex items-start gap-4 p-3 bg-background rounded-lg border">
          <GitCommit className="h-5 w-5 text-muted-foreground mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                {details.commit_info.sha}
              </code>
              <span className="text-sm flex items-center gap-1">
                <User className="h-3 w-3" />
                {details.commit_info.author_name}
              </span>
            </div>
            <p className="text-sm">{details.commit_info.message}</p>
          </div>
        </div>
      )}

      {/* Changed Files */}
      {details.changed_files && details.changed_files.length > 0 && (
        <div className="p-3 bg-background rounded-lg border">
          <div className="flex items-center gap-2 mb-2">
            <FileCode className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Changed Files ({details.changed_files.length})</span>
          </div>
          <div className="max-h-32 overflow-y-auto">
            {details.changed_files.map((file, idx) => (
              <code key={idx} className="block text-xs text-muted-foreground">
                {file}
              </code>
            ))}
          </div>
        </div>
      )}

      {/* Auto-remediation Info */}
      {details.details?.questions && details.details.questions.length > 0 && (
        <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                Auto-Remediation Questions
              </span>
            </div>
            {details.status === 'needs_human' && onRespond && (
              <div className="flex gap-2">
                {onDecline && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to decline this remediation? The deployment will remain failed.')) {
                        onDecline(details.id);
                      }
                    }}
                    className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                  >
                    <Ban className="h-3 w-3 mr-1" />
                    Decline
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => onRespond(details, details.details.questions)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Mail className="h-3 w-3 mr-1" />
                  Respond
                </Button>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {details.details.questions.map((q, idx) => (
              <div key={idx} className="bg-purple-100/50 dark:bg-purple-900/30 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold">
                    {idx + 1}
                  </span>
                  <p className="text-sm text-purple-700 dark:text-purple-300 font-medium flex-1">
                    {q}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remediation Output */}
      {details.remediation_output && (
        <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
              Auto-Remediation Output
            </span>
          </div>
          <pre className="text-xs text-purple-600 dark:text-purple-400 whitespace-pre-wrap max-h-64 overflow-y-auto bg-purple-100/50 dark:bg-purple-900/30 p-2 rounded">
            {details.remediation_output}
          </pre>
        </div>
      )}

      {/* Remediation Error */}
      {details.remediation_error && (
        <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-700 dark:text-red-300">
              Remediation Error
            </span>
          </div>
          <pre className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap max-h-40 overflow-y-auto">
            {details.remediation_error}
          </pre>
        </div>
      )}

      {/* Remediation Attempts */}
      {details.remediation_attempts && details.remediation_attempts.length > 0 && (
        <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
              Remediation Attempts ({details.remediation_attempts.length})
            </span>
          </div>
          <div className="space-y-2">
            {details.remediation_attempts.map((attempt, idx) => (
              <div key={idx} className="text-sm p-2 bg-purple-100/50 dark:bg-purple-900/30 rounded">
                <div className="flex items-center gap-2">
                  <Badge variant={attempt.success ? 'default' : 'destructive'} className="text-xs">
                    {attempt.success ? 'Success' : 'Failed'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {new Date(attempt.timestamp).toLocaleString()}
                  </span>
                </div>
                {attempt.questions && attempt.questions.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {attempt.questions.map((q, qIdx) => (
                      <div key={qIdx} className="text-xs bg-white dark:bg-gray-900 p-2 rounded border border-purple-200 dark:border-purple-700">
                        <span className="font-semibold text-purple-700 dark:text-purple-300">Q{qIdx + 1}:</span>{' '}
                        <span className="text-purple-600 dark:text-purple-400">{q}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logs */}
      {details.details?.logs && details.details.logs.length > 0 && (
        <div className="p-3 bg-background rounded-lg border">
          <div className="flex items-center gap-2 mb-2">
            <FileCode className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Build Logs</span>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {details.details.logs.map((log, idx) => (
              <div key={idx} className="text-xs">
                <Badge variant="outline" className="mb-1">{log.step}</Badge>
                {log.stderr && (
                  <pre className="text-red-600 dark:text-red-400 whitespace-pre-wrap ml-2">
                    {log.stderr.slice(-500)}
                  </pre>
                )}
                {log.stdout && (
                  <pre className="text-muted-foreground whitespace-pre-wrap ml-2">
                    {log.stdout.slice(-500)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeploymentsManager;
